import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { UserFields } from "models/interfaces";
import {
  getCallParams,
  getCredentialCallParams,
  getFileCallParams,
  getSearchedCallParams,
  makeCall,
  makeCallText,
} from "utils/service";
import { store } from "utils/store";

enum UserVerificationRequirement {
  REQUIRED = "required",
  PREFERRED = "preferred",
  DISCOURAGED = "discouraged",
}

export const fetchProfileDetails = async () => {
  try {
    const email = store.getState().auth.email;
    const url = `${urls.GET_USER_PROFILE}/${email}`;
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchRoles = async (limit?: number, page?: number) => {
  let url = `${urls.GET_ROLES}`;
  if (limit !== undefined && page !== undefined) {
    url = `${urls.GET_ROLES_BY_DOMAIN}/limit/${limit}/page/${page}`;
  }
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAppUsers = async (
  searchText: string,
  rowsPerPage: number,
  page: number
) => {
  const url = `${urls.GET_APP_USERS}/${rowsPerPage}/page/${page}`;
  const body = {
    entitySearch: {
      entity: "",
      searchText,
    },
    entitySorters: [],
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAppUsersCount = async (searchText: string) => {
  const url = `${urls.GET_APP_USERS_COUNT}`;
  const body = {
    entitySearch: {
      entity: "",
      searchText,
    },
    entitySorters: [],
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchExternalUsers = async (
  searchText: string,
  rowsPerPage: number,
  page: number
) => {
  const url = `${urls.GET_EXTERNAL_USERS}/${rowsPerPage}/page/${page}`;
  const body = {
    entitySearch: {
      entity: "",
      searchText,
    },
    entitySorters: [],
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchExternalUsersCount = async (searchText: string) => {
  const url = `${urls.GET_EXTERNAL_USERS_COUNT}`;
  const body = {
    entitySearch: {
      entity: "",
      searchText,
    },
    entitySorters: [],
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

const getSearchUrl = (tab: string, page: number, limit: number) => {
  switch (tab) {
    case strings.roleManagement:
      return `${urls.GET_ROLES_SEARCH}/limit/${limit}/page/${page}`;
    default:
      break;
  }
};

export const fetchSearchParams = async (
  tab: string,
  page: number,
  rowsPerPage: number,
  search: string
) => {
  const url: any = getSearchUrl(tab, page, rowsPerPage);
  try {
    const callParams = await getSearchedCallParams("POST", search);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

const getSearchCountUrl = (tab: string) => {
  switch (tab) {
    case strings.roleManagement:
      return `${urls.GET_ROLES_SEARCH_COUNT}`;
    default:
      break;
  }
};

export const fetchSearchCount = async (tab: string, search: string) => {
  const url: any = getSearchCountUrl(tab);
  try {
    const callParams = await getSearchedCallParams("POST", search);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const addUser = async (user: UserFields) => {
  try {
    const callParams = await getCallParams("POST", user);
    const response: any = await makeCall(urls.ADD_USER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateUser = async (user: UserFields) => {
  try {
    const callParams = await getCallParams("PATCH", user);
    const response: any = await makeCall(urls.UPDATE_USER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchResources = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      urls.GET_RESOURCES_BY_DOMAIN,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const addRole = async (user: any) => {
  try {
    const callParams = await getCallParams("POST", user);
    const response: any = await makeCall(urls.ADD_ROLE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateRole = async (user: any) => {
  try {
    const callParams = await getCallParams("POST", user);
    const response: any = await makeCall(urls.UPDATE_ROLE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchRolesCount = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(
      urls.GET_ROLES_BY_DOMAIN_COUNT,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchQRCodeForMFA = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.GET_QR_CODE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSecretKeyForMFA = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCallText(urls.GET_SECRET_KEY, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const verifyMFACode = async (
  password: string,
  authenticatorCode: string,
  multiFactorLogin: boolean
) => {
  try {
    const body = {
      email: store.getState().auth.email,
      pwd: password,
      authenticatorCode,
      multiFactorLogin,
    };
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.VERIFY_MFA, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateProfileDetails = async (body: any) => {
  try {
    const url = urls.UPDATE_USER;
    const callParams = await getCallParams("PATCH", body);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateProfilePicture = async (file: any) => {
  try {
    const url = urls.UPDATE_PROFILE_PICTURE;
    const callParams = await getFileCallParams("PATCH", file);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const removeProfilePicture = async () => {
  try {
    const callParams = await getCallParams("PATCH");
    const response: any = await makeCall(
      urls.REMOVE_PROFILE_PICTURE,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const savePreferences = async (data: any) => {
  try {
    const callParams = await getCallParams("POST", data);
    const response: any = await makeCall(urls.SAVE_PREFERENCES, callParams);
  } catch (error: any) {
    throw error;
  }
};

export const fetchManagers = async () => {
  const url = `${urls.GET_APP_USERS}/all/page/-1`;
  const body = {
    entitySearch: {
      entity: "",
      searchText: "",
    },
    entitySorters: [],
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const resetMFA = async (email: string) => {
  try {
    const url = `${urls.RESET_MFA}/${email}`;
    const callParams = await getCallParams("PATCH");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const unlockUser = async (email: string) => {
  try {
    const url = `${urls.UNLOCK_USER}/${email}`;
    const callParams = await getCallParams("PATCH");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const doAddLocation = async (locationData: any) => {
  try {
    const callParams = await getCallParams("POST", locationData);
    const response: any = await makeCall(urls.ADD_LOCATION, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSessions = async () => {
  try {
    const url = urls.GET_USER_SESSIONS;
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const inactiveSession = async (userId: string, authToken: string) => {
  try {
    const body = {
      id:userId,
      authToken,
    };
    const url = urls.INACTIVATE_SESSION;
    const callParams = await getCallParams("PATCH", body);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const marketingSubscriber = async (body: any) => {
  try {
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.MARKERING_SUBSCRIBER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getCredentialOptions = async () => {
  try {
    const callParams = await getCredentialCallParams("POST");
    const credentialGetJson = await makeCall(urls.PASSKEY_LOGIN, callParams);
    const publicKeyCredentialOptions = {
      publicKey: {
        challenge: base64urlToUint8array(credentialGetJson.publicKey.challenge),
        userVerification: UserVerificationRequirement.REQUIRED,
        rpId: credentialGetJson.publicKey.rpId,
        allowCredentials: [],
      },
    };

    let publicKeyCredential;

    try {
      publicKeyCredential = (await navigator.credentials.get({
        publicKey: publicKeyCredentialOptions.publicKey,
      })) as PublicKeyCredential;

      if (!publicKeyCredential) {
        throw new Error("Failed to obtain publicKeyCredential");
      }
    } catch (error: any) {
      if (
        error.message.includes(
          "The operation either timed out or was not allowed"
        )
      ) {
        return;
      }
      throw error;
    }

    const assertionResponse =
      publicKeyCredential.response as AuthenticatorAssertionResponse;

    const encodedResult = {
      type: publicKeyCredential.type,
      id: publicKeyCredential.id,
      response: {
        authenticatorData: uint8arrayToBase64url(
          new Uint8Array(assertionResponse.authenticatorData)
        ),
        clientDataJSON: uint8arrayToBase64url(
          new Uint8Array(assertionResponse.clientDataJSON)
        ),
        signature: uint8arrayToBase64url(
          new Uint8Array(assertionResponse.signature)
        ),
        userHandle: assertionResponse.userHandle
          ? uint8arrayToBase64url(new Uint8Array(assertionResponse.userHandle))
          : null,
      },
      clientExtensionResults: publicKeyCredential.getClientExtensionResults(),
    };

    try {
      const finishUrl = `${urls.PASSKEY_FINISH_LOGIN}/${credentialGetJson.publicKey.challenge}`;
      const callParams = await getCredentialCallParams("POST", encodedResult);
      const finishResponse = await makeCall(finishUrl, callParams);
      return finishResponse;
    } catch (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

function uint8arrayToBase64url2(array: Uint8Array): string {
  return btoa(String.fromCharCode.apply(null, array as any))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

const uint8arrayToBase64url = (buffer: ArrayBuffer) => {
  const base64 = btoa(
    String.fromCharCode(...Array.from(new Uint8Array(buffer)))
  );
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const base64urlToUint8array = (base64url: string) => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = 4 - (base64.length % 4);
  const padded = base64 + "====".slice(0, padLength);
  const raw = atob(padded);
  const array = Array.from(raw);
  return new Uint8Array(array.map((char) => char.charCodeAt(0)));
};

export const getCredentialCreationOptions = async (displayName: string) => {
  try {
    const url = `${urls.PASSKEY_REGISTER}/${displayName}`;
    const callParams = await getCredentialCallParams("POST");
    const response = await makeCall(url, callParams);
    const credentialCreateJson = response;

    credentialCreateJson.excludeCredentials.forEach((credential: any) => {
      credential.id = base64urlToUint8array(credential.id);
    });
    const publicKey = {
      challenge: base64urlToUint8array(credentialCreateJson.challenge),
      rp: {
        id: credentialCreateJson.rp.id,
        name: credentialCreateJson.rp.name,
      },
      user: {
        id: base64urlToUint8array(credentialCreateJson.user.id),
        name: credentialCreateJson.user.name,
        displayName: credentialCreateJson.user.displayName,
      },
      pubKeyCredParams: credentialCreateJson.pubKeyCredParams,
      authenticatorSelection: credentialCreateJson.authenticatorSelection,
      extension: credentialCreateJson.extension,
      excludeCredentials: credentialCreateJson.excludeCredentials,
    };
    return { publicKey };
  } catch (error: any) {
    if (
      error.message.includes(
        "The operation either timed out or was not allowed"
      )
    ) {
      return;
    }
    return new Error(
      "Failed to fetch credential creation options. Please try again later."
    );
  }
};

export const createPasskey = async (options: any) => {
  const response = (await navigator.credentials.create(options)) as any;

  const publicKeyCredential = {
    type: response.type,
    id: response.id,
    rawId: uint8arrayToBase64url2(new Uint8Array(response.rawId)),
    response: {
      attestationObject: uint8arrayToBase64url2(
        new Uint8Array(response.response.attestationObject)
      ),
      clientDataJSON: uint8arrayToBase64url2(
        new Uint8Array(response.response.clientDataJSON)
      ),
      transports: response.response.getTransports
        ? response.response.getTransports()
        : [],
    },
    clientExtensionResults: response.getClientExtensionResults(),
  };

  const registrationnesponse: any = await fetch(
    `${urls.PASSKEY_FINISH_REGISTER}`,
    {
      credentials: "include",
      method: "POST",
      body: JSON.stringify(publicKeyCredential),
      headers: { Authorization: "Bearer " + store.getState().auth.accessToken },
    }
  );

  return await registrationnesponse.json();
};

export const fetchPasskeys = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.GET_ALL_PASSKEY, callParams);
    return response;
  } catch (error) {
    return error;
  }
};

export const deletePasskey = async (passkeyData: any) => {
  try {
    const callParams = await getCallParams("DELETE", passkeyData);
    const response = await makeCall(urls.DELETE_PASSKEY, callParams);
    return response;
  } catch (error) {
    return error;
  }
};

export const editPasskeyName = async (passkeyData: any) => {
  try {
    const callParams = await getCallParams("PATCH", passkeyData);
    const response = await makeCall(urls.UPDATE_PASSKEY, callParams);
    return response;
  } catch (error) {
    return error;
  }
};

export const initiateAccountClosure = async (currentPassword: string) => {
  try {
    const body = {
      currentPassword,
      confirm: true,
    };
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(urls.INITIATE_ACCOUNT_CLOSURE, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};